import React, { useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  InputBase,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Search,
  MoreVert,
  Close,
  ArrowBackIosNew,
  Phone,
} from "@mui/icons-material";
import ProfileSidebar from "../../Profile";

const ChatHeader = ({
  contact,
  getInitials,
  onSearchChange,
  onBack,
  onUpdateContactName,
}) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const isMobile = !useMediaQuery(theme.breakpoints.up("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleMenuClose = () => setAnchorEl(null);

  const handleViewContact = () => {
    handleMenuClose();
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchTerm("");
      onSearchChange("");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    onSearchChange(e.target.value);
  };

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        position: "relative",
        transition: "all 0.2s ease",
      }}
    >
      {/* Main Header Row */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 72,
        }}
      >
        {/* Left Section - Back Button + Avatar + Contact Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: 1 }}>
          {/* Back Button (Mobile) */}
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{
                color: "#64748b",
                width: 36,
                height: 36,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: "#f1f5f9",
                  color: "#475569",
                },
              }}
            >
              <ArrowBackIosNew sx={{ fontSize: 18 }} />
            </IconButton>
          )}

          {/* Avatar with Status Badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              p: 0.5,
              borderRadius: "12px",
              transition: "all 0.2s ease",
              minWidth: 0,
              "&:hover": {
                bgcolor: "#f8fafc",
              },
            }}
            onClick={handleViewContact}
          >
            <Avatar
              src={contact.profile_pic}
              sx={{
                width: 48,
                height: 48,
                fontSize: "1rem",
                fontWeight: 600,
                bgcolor: primaryColor,
                border: `2px solid ${alpha(primaryColor, 0.2)}`,
                transition: "all 0.2s ease",
              }}
            >
              {getInitials(contact.client_name || contact.phone)}
            </Avatar>

            {/* Contact Name and Status */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#1e293b",
                  lineHeight: 1.3,
                  letterSpacing: "-0.01em",
                }}
              >
                {contact.client_name || contact.phone}
              </Typography>
              {/* <Typography
                sx={{
                  color: "#10b981",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  mt: 0.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                    display: "inline-block",
                  }}
                />
                Active now
              </Typography> */}
            </Box>
          </Box>
        </Box>

        {/* Right Section - Search + Call + Menu Icons */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {/* Search Input (Expanded) - Desktop only inline */}
          {!isMobile && (
            <Fade in={isSearchOpen} unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#f1f5f9",
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.75,
                  mr: 1,
                  width: 220,
                  border: "1px solid #e2e8f0",
                  transition: "all 0.2s ease",
                  "&:focus-within": {
                    borderColor: primaryColor,
                    boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.1)}`,
                  },
                }}
              >
                <Search sx={{ fontSize: 18, color: "#94a3b8", mr: 1 }} />
                <InputBase
                  autoFocus
                  fullWidth
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    fontSize: "0.875rem",
                    color: "#334155",
                    "& input::placeholder": {
                      color: "#94a3b8",
                      opacity: 1,
                    },
                  }}
                />
                <IconButton
                  size="small"
                  onClick={toggleSearch}
                  sx={{
                    p: 0.25,
                    color: "#64748b",
                    "&:hover": { color: "#475569" },
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Fade>
          )}

          {/* Search Icon */}
          {!isSearchOpen && (
            <IconButton
              onClick={toggleSearch}
              sx={{
                color: "#64748b",
                width: 40,
                height: 40,
                borderRadius: "10px",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: primaryColor,
                  bgcolor: alpha(primaryColor, 0.08),
                },
              }}
            >
              <Search sx={{ fontSize: 22 }} />
            </IconButton>
          )}

          {/* Close Search Icon - Mobile only (shown when search is open) */}
          {isMobile && isSearchOpen && (
            <IconButton
              onClick={toggleSearch}
              sx={{
                color: "#64748b",
                width: 40,
                height: 40,
                borderRadius: "10px",
                transition: "all 0.2s ease",
                "&:hover": {
                  color: primaryColor,
                  bgcolor: alpha(primaryColor, 0.08),
                },
              }}
            >
              <Close sx={{ fontSize: 22 }} />
            </IconButton>
          )}

          {/* Call Icon */}
          <IconButton
            sx={{
              color: "#64748b",
              width: 40,
              height: 40,
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#10b981",
                bgcolor: "rgba(16, 185, 129, 0.08)",
              },
            }}
          >
            <Phone sx={{ fontSize: 22 }} />
          </IconButton>

          {/* More Options Menu */}
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              color: "#64748b",
              width: 40,
              height: 40,
              borderRadius: "10px",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#f1f5f9",
                color: "#475569",
              },
            }}
          >
            <MoreVert sx={{ fontSize: 22 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Mobile Search Row - Appears below header on next line */}
      {isMobile && isSearchOpen && (
        <Fade in timeout={200}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "#f1f5f9",
              borderRadius: "12px",
              mx: 2,
              mb: 1,
              px: 1.5,
              py: 0.75,
              border: "1px solid #e2e8f0",
              "&:focus-within": {
                borderColor: primaryColor,
                boxShadow: `0 0 0 3px ${alpha(primaryColor, 0.1)}`,
              },
            }}
          >
            <Search sx={{ fontSize: 18, color: "#94a3b8", mr: 1 }} />
            <InputBase
              autoFocus
              fullWidth
              placeholder="Search messages..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                fontSize: "0.875rem",
                color: "#334155",
                "& input::placeholder": {
                  color: "#94a3b8",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Fade>
      )}

      {/* Profile Sidebar Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        elevation={0}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1.5, // margin-top: 12px
            minWidth: 380, // min-width: 380px
            borderRadius: "16px",
            boxShadow: "0 20px 40px -8px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(0, 0, 0, 0.06)",
            overflow: "hidden",
            p: 0,
          },
        }}
      >
        <ProfileSidebar
          key={`${contact?._id || contact?.id}-${contact?.client_name || ""}`}
          lead={contact}
          onClose={handleMenuClose}
          onUpdateContactName={onUpdateContactName}
        />
      </Menu>
    </Box>
  );
};

export default ChatHeader;
